import { useState } from 'react';
import { useLogin, useNotify } from 'react-admin';
import { Box, Card, CardContent, TextField, Button, Typography, Stack, CircularProgress, Alert, InputAdornment, IconButton } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { tokens } from '../theme.js';

export const LoginPage = () => {
  const login = useLogin();
  const notify = useNotify();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await login({ username, password });
    } catch (err) {
      const msg = err?.message || 'Sign-in failed';
      setError(msg);
      notify(msg, { type: 'error' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 3,
        // Cream gradient + faint green vignette
        background: `radial-gradient(ellipse at top, ${tokens.cream2} 0%, ${tokens.cream} 55%, ${tokens.cream3} 100%)`,
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          background: `linear-gradient(135deg, transparent 0%, transparent 60%, ${tokens.green900} 200%)`,
          opacity: 0.06,
          pointerEvents: 'none',
        },
      }}
    >
      <Card
        sx={{
          width: '100%',
          maxWidth: 420,
          p: { xs: 3, sm: 4 },
          border: `1px solid ${tokens.line}`,
          borderTop: `4px solid ${tokens.gold}`,
          boxShadow: '0 12px 40px rgba(14, 42, 31, 0.12), 0 4px 12px rgba(14, 42, 31, 0.06)',
          background: tokens.paper,
          position: 'relative',
        }}
      >
        <CardContent sx={{ p: 0 }}>
          {/* Brand */}
          <Stack alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
            <Box
              component="img"
              src="/mlis-logo.jpg"
              alt="MLIS"
              sx={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                border: `2px solid ${tokens.gold}`,
                boxShadow: `0 0 0 4px ${tokens.cream2}`,
              }}
            />
            <Box sx={{ textAlign: 'center' }}>
              <Typography
                sx={{
                  fontFamily: tokens.serif,
                  fontWeight: 500,
                  fontSize: '1.6rem',
                  color: tokens.ink,
                  lineHeight: 1.1,
                }}
              >
                Maple Leaf
              </Typography>
              <Typography
                sx={{
                  color: tokens.gold700,
                  fontSize: 11,
                  letterSpacing: '0.22em',
                  textTransform: 'uppercase',
                  fontWeight: 600,
                  mt: 0.5,
                }}
              >
                Content Management
              </Typography>
            </Box>
          </Stack>

          <Typography
            variant="body2"
            sx={{ textAlign: 'center', color: tokens.ink3, mb: 3 }}
          >
            Sign in to manage the school website.
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2, fontSize: 13 }}>{error}</Alert>
          )}

          <form onSubmit={submit}>
            <Stack spacing={2}>
              <TextField
                label="Email"
                type="email"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                disabled={busy}
                fullWidth
                autoFocus
                required
                size="medium"
              />
              <TextField
                label="Password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={busy}
                fullWidth
                required
                size="medium"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        edge="end"
                        disabled={busy}
                        onClick={() => setShowPassword((v) => !v)}
                        // Keep the caret in the field when the icon is clicked.
                        onMouseDown={(e) => e.preventDefault()}
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                      >
                        {showPassword
                          ? <VisibilityOffIcon fontSize="small" />
                          : <VisibilityIcon fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                type="submit"
                variant="contained"
                color="primary"
                size="large"
                disabled={busy || !username || !password}
                sx={{
                  py: 1.25,
                  fontSize: 15,
                  fontWeight: 700,
                  letterSpacing: '0.04em',
                }}
              >
                {busy ? <CircularProgress size={20} sx={{ color: tokens.ink }} /> : 'Sign in'}
              </Button>
            </Stack>
          </form>

          <Typography
            variant="caption"
            sx={{
              display: 'block',
              textAlign: 'center',
              mt: 3,
              color: tokens.ink4,
              fontSize: 11,
              lineHeight: 1.6,
            }}
          >
            Maple Leaf International School · Established 1972<br />
            Forgot your password? Contact the IT administrator.
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
};
